import { type Row } from '@tanstack/react-table';
import { useCallback } from 'react';

import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import type { DataTableRowData } from '../../row-data-types.js';
import type { TDataTableFeatures } from '../types/features.types.js';
import type { DataTableOptions } from '../types/table.types.js';
import type { DataTableRowOrderProps } from './row-order-types.js';

/**
 * Typed shape of the `rowOrdering` object prop accepted by `DataTable`.
 */
interface RowOrderConfigObject<TData> {
  /**
   * Renders the drag-handle column when `true`.
   */
  enableDragAndDrop?: boolean;
  /**
   * Returns `true` for rows whose drag handle should be disabled.
   */
  disableRowDragAndDrop?: (row: TData) => boolean;
  /**
   * Tooltip shown on disabled drag handles. A string is reused for every
   * disabled row; a function receives the row data and can return a per-row
   * message (or `undefined` for no tooltip).
   *
   * @remarks If passing a function, ensure it is stable across renders
   * (e.g. wrap it with `useCallback` or define it outside the component) —
   * an unstable reference triggers unnecessary re-renders.
   */
  disabledRowDragAndDropTooltip?: string | ((row: TData) => string | undefined);
}

/**
 * Checks if the `rowOrdering` prop is a configuration object.
 * @param rowOrdering - The table prop that enables row ordering.
 * @returns `true` if the param is an object, `false` otherwise.
 */
function isRowOrderConfigObject<TData>(
  rowOrdering: boolean | RowOrderConfigObject<TData>,
): rowOrdering is RowOrderConfigObject<TData> {
  return typeof rowOrdering === 'object';
}

/**
 * Configuration hook for the row order in the DataTable.
 * @internal
 */
export function useRowOrder<TData extends DataTableRowData>(
  props: DataTableRowOrderProps<TData>,
  options: DataTableOptions<Exclude<TData, unknown>>,
): void {
  const { rowOrdering } = props;

  const disableRowDragAndDropFn =
    typeof rowOrdering === 'object'
      ? rowOrdering.disableRowDragAndDrop
      : undefined;

  const enableRowDragAndDropCallback = useCallback(
    (row: Row<TDataTableFeatures, Exclude<TData, unknown>>) => {
      // If no disable function is provided, enable drag and drop for all rows
      if (!disableRowDragAndDropFn) {
        return true;
      }
      return !disableRowDragAndDropFn(row.original);
    },
    [disableRowDragAndDropFn],
  );

  const tooltipOption =
    typeof rowOrdering === 'object'
      ? rowOrdering.disabledRowDragAndDropTooltip
      : undefined;

  const disabledRowDragAndDropTooltipCallback = useCallback(
    (row: Row<TDataTableFeatures, Exclude<TData, unknown>>) =>
      typeof tooltipOption === 'function'
        ? tooltipOption(row.original)
        : tooltipOption,
    [tooltipOption],
  );

  const [rowOrder, onRowOrderChange] = useControllableState<string[]>({
    value: props.rowOrder,
    defaultValue: props.defaultRowOrder,
    onChange: props.onRowOrderChange,
  });

  options.enableRowOrderDragAndDrop = false;
  if (rowOrdering === false || rowOrdering === undefined) {
    options.enableRowOrdering = false;
    return;
  }

  options.enableRowOrdering = true;
  options.onRowOrderChange = onRowOrderChange;
  options.state = { ...options.state, rowOrder };

  // config object is given
  if (isRowOrderConfigObject(rowOrdering)) {
    if (
      rowOrdering.enableDragAndDrop === true &&
      rowOrdering.disableRowDragAndDrop !== undefined
    ) {
      options.lockDisabledRows = rowOrdering.lockDisabledRows;
      options.enableRowOrderDragAndDrop = enableRowDragAndDropCallback;
      options.disabledRowDragAndDropTooltip =
        disabledRowDragAndDropTooltipCallback;
      options.autoResetPageIndex = false;
    } else {
      options.enableRowOrderDragAndDrop =
        rowOrdering.enableDragAndDrop === true;
    }
  }
}
